import { Client, Functions, ExecutionMethod } from 'appwrite';
import { CampaignReportCandidate } from '../utils/leadReports';

export interface CampaignReportExportRequest {
  campaignId: string;
  campaignName: string;
  signature: string;
  candidates: CampaignReportCandidate[];
  userEmail?: string | null;
}

export interface CampaignReportExportResult {
  success: boolean;
  downloadUrl?: string;
  downloadUrls?: string[];
  zipFileId?: string;
  zipFileIds?: string[];
  partCount?: number;
  exportedCount?: number;
  missingCount?: number;
  missingFileIds?: string[];
  generatedAt?: string;
  signature?: string;
  error?: string;
}

class CampaignReportExportService {
  private client: Client;
  private functions: Functions;
  private functionId: string;

  constructor() {
    this.client = new Client()
      .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
      .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '');

    this.functions = new Functions(this.client);
    this.functionId = import.meta.env.VITE_APPWRITE_CAMPAIGN_REPORT_EXPORT_FUNCTION_ID || 'campaign-report-export';
  }

  private getFriendlyErrorMessage(error: any): string {
    const rawMessage = String(error?.message || '').trim();

    if (rawMessage.includes('Function with the requested ID could not be found')) {
      return 'The campaign export service is not deployed in this Appwrite project yet.';
    }

    return rawMessage || 'Failed to export campaign reports';
  }

  async exportCampaignReports(payload: CampaignReportExportRequest): Promise<CampaignReportExportResult> {
    try {
      const execution = await this.functions.createExecution(
        this.functionId,
        JSON.stringify(payload),
        false,
        '/',
        ExecutionMethod.POST,
      );

      const responseBody = JSON.parse(execution.responseBody || '{}') as CampaignReportExportResult;
      if (!responseBody.success) {
        throw new Error(responseBody.error || 'Failed to export campaign reports');
      }

      return responseBody;
    } catch (error: any) {
      return {
        success: false,
        error: this.getFriendlyErrorMessage(error),
      };
    }
  }
}

export const campaignReportExportService = new CampaignReportExportService();
